package com.arena_web.ui.theme

import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.silk.theme.colors.ColorMode

/**
 * Current sport color scheme — provided at layout level, consumed everywhere.
 * Default: Football.
 */
val LocalSportColors = compositionLocalOf { SportColors.Football }

// =============================================================================
// Color utility extensions
// =============================================================================

fun Color.Companion.hex(hex: String): Color {
    val cleanHex = hex.removePrefix("#").uppercase()
    require(cleanHex.length == 6) { "Hex color must be 6 characters (e.g., '7A9DE4')" }
    return Color.rgb(
        cleanHex.substring(0, 2).toInt(16),
        cleanHex.substring(2, 4).toInt(16),
        cleanHex.substring(4, 6).toInt(16)
    )
}

fun Color.Companion.hexa(hex: String, alpha: Float): Color {
    val cleanHex = hex.removePrefix("#").uppercase()
    require(cleanHex.length == 6) { "Hex color must be 6 characters (e.g., '7A9DE4')" }
    return Color.rgba(
        cleanHex.substring(0, 2).toInt(16),
        cleanHex.substring(2, 4).toInt(16),
        cleanHex.substring(4, 6).toInt(16),
        alpha
    )
}

fun Color.toRgbString(): String {
    val rgb = this.toRgb()
    return "rgb(${rgb.red}, ${rgb.green}, ${rgb.blue})"
}

fun Color.toHexString(): String {
    val rgb = this.toRgb()
    fun toHex(n: Int): String {
        val hex = n.toString(16).uppercase()
        return if (hex.length == 1) "0$hex" else hex
    }
    return "${toHex(rgb.red)}${toHex(rgb.green)}${toHex(rgb.blue)}"
}

fun Color.toRgbaString(alpha: Float): String {
    val rgb = this.toRgb()
    return "rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, $alpha)"
}

fun Color.withAlpha(alpha: Float): Color {
    val rgb = this.toRgb()
    return Color.rgba(rgb.red, rgb.green, rgb.blue, alpha)
}

// =============================================================================
// App color system — based on Figma design tokens
// =============================================================================

object AppColors {

    // -------------------------------------------------------------------------
    // Primary brand colors
    // -------------------------------------------------------------------------
    val Primary = Color.hex("0061FF")           // Football / Primary
    val PrimaryWhite = Color.hex("FFFFFF")
    val PrimaryBlack = Color.hex("000000")
    val PrimaryYellow = Color.hex("E4CD40")
    val PrimaryMint = Color.hex("00E5A4")
    val SecondaryMint = Color.hex("00C288")

    val Error = Color.hex("FF1A1A")

    // -------------------------------------------------------------------------
    // Chat button (existing — kept for backward compatibility)
    // -------------------------------------------------------------------------
    val ChatButtonBackground = Color.hexa("FFFFFF", 0.04f)
    val ChatButtonGradientStart = Color.hex("152265")
    val ChatButtonGradientEnd = Color.hexa("212144", 0f)
    val ChatButtonShadowInner1 = Color.hexa("FFFFFF", 0.06f)
    val ChatButtonShadowInner2 = Color.hexa("FFFFFF", 0.08f)
    val ChatButtonShadowDrop = Color.hexa("FFFFFF", 0.2f)

    // -------------------------------------------------------------------------
    // Icon filter: white SVG → #B4CCF6 (default icon tint)
    // -------------------------------------------------------------------------
    const val IconDefaultFilter = "brightness(0) invert(82%) sepia(15%) saturate(700%) hue-rotate(190deg) brightness(1.05)"

    // Icon filter: white SVG → #FF4242 (destructive / delete tint)
    const val IconDestructiveFilter = "brightness(0) invert(38%) sepia(91%) saturate(5000%) hue-rotate(345deg) brightness(101%) contrast(101%)"

    /** Success-green (~#22C55E) tint for status icons (finished matches). */
    const val IconSuccessFilter = "brightness(0) invert(62%) sepia(52%) saturate(577%) hue-rotate(88deg) brightness(95%) contrast(90%)"

    // -------------------------------------------------------------------------
    // Text colors (dark theme)
    //
    // ⚠ LIGHT-THEME MIGRATION: these are dark-only constants. Any surface
    // that must adapt to the light theme should use ThemeVars instead
    // (ThemeVars.Text1/2/3 or the *_V string constants) — the CSS tokens
    // flip automatically with data-theme. Same rule for icon tints: replace
    // hardcoded `filter(AppColors.IconDefaultFilter)` with
    // ThemeVars.ICON_FILTER_V wherever the icon sits on a themed surface.
    // Keep AppColors only for genuinely theme-invariant colors (brand blue,
    // sport palettes, status colors, scrims inside photos).
    // -------------------------------------------------------------------------
    object Text {
        val Primary = Color.hex("B4CCF6")
        val Secondary = Color.hex("BED6F5")
        val Muted = Color.hex("959697")
        val OnSurfaceMuted = Color.hex("99B1BF")
        val Subtle = Color.hex("B9B9B9")
        val NavLabelInactive = Color.hex("CCCDCE")
    }

    // -------------------------------------------------------------------------
    // UI / Surface colors
    // -------------------------------------------------------------------------
    val ChartTooltipFill = Color.hex("112140")
    val BackgroundMatchBase = Color.hexa("FFFFFF", 0.05f)
    val TabDim1 = Color.hex("AABBDD")
    val TabDim2 = Color.hex("AABBDD")

    // -------------------------------------------------------------------------
    // Result overlay colors
    // -------------------------------------------------------------------------
    object Result {
        val Win = Color.hexa("0A36F7", 0.16f)
        val Draw = Color.hexa("67C476", 0.10f)
        val Loss = Color.hexa("F72A0A", 0.20f)
    }

    // -------------------------------------------------------------------------
    // Interactive
    // -------------------------------------------------------------------------
    val InteractivePrimaryFootball = Color.hexa("000000", 0.10f)
    val InteractivePrimaryTennis = Color.hex("053F00")
    val InteractivePrimaryVolleyball = Color.hexa("FFFF36", 0.10f)
    val InteractivePrimaryBasketball = Color.hexa("FF5500", 0.90f)
    val InteractivePrimaryPadel = Color.hex("0A8C8E")

    // -------------------------------------------------------------------------
    // Light theme
    // -------------------------------------------------------------------------
    object Light {
        val Background = Color.hex("FFFFFF")
        val Surface = Color.hex("FAFAFA")
        val SurfaceVariant = Color.hex("F5F5F5")
        val OnBackground = Color.hex("000000")
        val OnSurface = Color.hex("000000")
        val OnPrimary = Color.hex("FFFFFF")

        val Border = Color.hex("E9E9E9")
        val Divider = Color.hex("E0E0E0")

        val TextPrimary = Color.hex("000000")
        val TextSecondary = Color.hex("646464")
        val TextTertiary = Color.hex("969696")

        val Shadow = Color.hexa("000000", 0.1f)
        val Overlay = Color.hexa("000000", 0.5f)
    }

    // -------------------------------------------------------------------------
    // Dark theme
    // -------------------------------------------------------------------------
    object Dark {
        val Background = Color.hex("02040B")
        val Surface = Color.hex("0F172A")
        val SurfaceVariant = Color.hex("112140")
        val Sidebar = Color.hexa("1C3587", 0.12f)
        val OnBackground = Color.hex("FFFFFF")
        val OnSurface = Color.hex("FFFFFF")
        val OnPrimary = Color.hex("FFFFFF")

        val Border = Color.hex("282D3C")
        val BorderLight = Color.hex("1E2332")
        val Divider = Color.hex("323746")

        val TextPrimary = Color.hex("B4CCF6")
        val TextSecondary = Color.hex("BED6F5")
        val TextTertiary = Color.hex("959697")

        val Shadow = Color.hexa("000000", 0.4f)
        val Overlay = Color.hexa("000000", 0.7f)
    }

    // -------------------------------------------------------------------------
    // Theme accessor
    // -------------------------------------------------------------------------
    val DarkTheme = ThemeColors(
        background = Dark.Background,
        surface = Dark.Surface,
        surfaceVariant = Dark.SurfaceVariant,
        onBackground = Dark.OnBackground,
        onSurface = Dark.OnSurface,
        onPrimary = Dark.OnPrimary,
        border = Dark.Border,
        divider = Dark.Divider,
        textPrimary = Dark.TextPrimary,
        textSecondary = Dark.TextSecondary,
        textTertiary = Dark.TextTertiary,
        shadow = Dark.Shadow,
        overlay = Dark.Overlay
    )

    fun getColors(colorMode: ColorMode): ThemeColors {
        return when (colorMode) {
            ColorMode.LIGHT -> ThemeColors(
                background = Light.Background,
                surface = Light.Surface,
                surfaceVariant = Light.SurfaceVariant,
                onBackground = Light.OnBackground,
                onSurface = Light.OnSurface,
                onPrimary = Light.OnPrimary,
                border = Light.Border,
                divider = Light.Divider,
                textPrimary = Light.TextPrimary,
                textSecondary = Light.TextSecondary,
                textTertiary = Light.TextTertiary,
                shadow = Light.Shadow,
                overlay = Light.Overlay
            )
            ColorMode.DARK -> DarkTheme
        }
    }
}

data class ThemeColors(
    val background: Color,
    val surface: Color,
    val surfaceVariant: Color,
    val onBackground: Color,
    val onSurface: Color,
    val onPrimary: Color,
    val border: Color,
    val divider: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textTertiary: Color,
    val shadow: Color,
    val overlay: Color
)

// =============================================================================
// Sport color system
// =============================================================================

data class SportColorScheme(
    val primary: Color,
    val onPrimary: Color,               // Content color on primary bg (white for football, black for others)
    val secondary: Color,
    val onSecondary: Color,             // Content color on secondary bg
    val metricsPrimary: Color,
    val interactivePrimary: Color,
    val radarFill: Color,
    val radarFillOpacity: Float,
    val dataPoint: Color,
    val dataPointInner: Color,
    val chartAreaBase: Color,
    // Stroke tokens
    val chartLineStroke: Color,          // 6px center stroke for chart lines
    val radarSecondaryStroke: Color,     // 1.2px center stroke for radar outlines
    val interactiveStrokeColor: Color,   // 0.8px gradient start for bar chart outlines
    val barBorderColor: String,           // Bar chart border color
    val barBackground: String,            // Bar chart background
    val barBoxShadow: String              // Bar chart inner shadow (uses s() at call site)
) {
    /** CSS filter to tint icons to match onPrimary color */
    val iconFilterOnPrimary: String get() = if (onPrimary == Color.hex("000000")) "brightness(0)" else "brightness(0) invert(1)"
    /** CSS filter to tint icons to match onSecondary color */
    val iconFilterOnSecondary: String get() = if (onSecondary == Color.hex("000000")) "brightness(0)" else "brightness(0) invert(1)"
}

object SportColors {

    val Football = SportColorScheme(
        primary = Color.hex("0061FF"),
        onPrimary = Color.hex("FFFFFF"),
        secondary = Color.hex("004DCD"),
        onSecondary = Color.hex("FFFFFF"),
        metricsPrimary = Color.hex("1E7FF5"),
        interactivePrimary = Color.hexa("000000", 0.10f),
        radarFill = Color.hex("0061FF"),
        radarFillOpacity = 0.15f,
        dataPoint = Color.hex("0061FF"),
        dataPointInner = Color.hex("0C1787"),
        chartAreaBase = Color.hex("0C1787"),
        chartLineStroke = Color.hex("0C1787"),
        radarSecondaryStroke = Color.hex("72A5FF"),
        interactiveStrokeColor = Color.hex("1439B9"),
        barBorderColor = "#0c42ff",
        barBackground = "rgba(0, 0, 0, 0.1)",
        barBoxShadow = "inset 0 {1.84} {5.52} #5861C3, inset 0 {14.73} {22.09} #3140EB, inset 0 {44.19} {31.32} {7.83} #0816A4"
    )

    val Tennis = SportColorScheme(
        primary = Color.hex("7BFA6F"),
        onPrimary = Color.hex("000000"),
        secondary = Color.hex("34D524"),
        onSecondary = Color.hex("000000"),
        metricsPrimary = Color.hex("7BFA6F"),
        interactivePrimary = Color.hex("053F00"),
        radarFill = Color.hex("7BFA6F"),
        radarFillOpacity = 0.10f,
        dataPoint = Color.hex("7BFA6F"),
        dataPointInner = Color.hex("0D7903"),
        chartAreaBase = Color.hex("0D7903"),
        chartLineStroke = Color.hex("0D7903"),
        radarSecondaryStroke = Color.hex("C2F1A9"),
        interactiveStrokeColor = Color.hex("2EF314"),
        barBorderColor = "#2ef314",
        barBackground = "#053f00",
        barBoxShadow = "inset 0 {1.84} {5.52} #33F202, inset 0 {14.73} {22.09} #18A60B, inset 0 {44.19} {31.32} {7.83} #0C7A02"
    )

    val Volleyball = SportColorScheme(
        primary = Color.hex("FFFF54"),
        onPrimary = Color.hex("000000"),
        secondary = Color.hex("F5DE15"),
        onSecondary = Color.hex("000000"),
        metricsPrimary = Color.hex("C6F801"),
        interactivePrimary = Color.hexa("FFFF36", 0.10f),
        radarFill = Color.hex("C6F801"),
        radarFillOpacity = 0.10f,
        dataPoint = Color.hex("AEA725"),
        dataPointInner = Color.hex("FFFF54"),
        chartAreaBase = Color.hex("AEA725"),
        chartLineStroke = Color.hex("AEA725"),
        radarSecondaryStroke = Color.hex("FFFFAA"),
        interactiveStrokeColor = Color.hex("F7F70A"),
        barBorderColor = "#f7f70a",
        barBackground = "rgba(255, 255, 54, 0.1)",
        barBoxShadow = "inset 0 {3} {18} rgba(184,184,59,0.9), inset 0 {45} {26} {6} rgba(165,165,43,0.02), inset 0 {161} {56} {5} rgba(216,202,10,0.72)"
    )

    val Basketball = SportColorScheme(
        primary = Color.hex("FFA438"),
        onPrimary = Color.hex("000000"),
        secondary = Color.hex("E88908"),
        onSecondary = Color.hex("000000"),
        metricsPrimary = Color.hex("FFB84C"),
        interactivePrimary = Color.hexa("FF5500", 0.90f),
        radarFill = Color.hex("FFA438"),
        radarFillOpacity = 0.10f,
        dataPoint = Color.hex("C6590C"),
        dataPointInner = Color.hex("FFA438"),
        chartAreaBase = Color.hex("C6590C"),
        chartLineStroke = Color.hex("C6590C"),
        radarSecondaryStroke = Color.hex("FFCE93"),
        interactiveStrokeColor = Color.hex("EA8E22"),
        barBorderColor = "#ea8e22",
        barBackground = "rgba(255, 85, 0, 0.9)",
        barBoxShadow = "inset 0 {2} {5.5} rgba(238,140,3,0.6), inset 0 {15} {22} rgba(209,106,32,0.8), inset 0 {161} {56} {5} rgba(160,94,14,0.5)"
    )

    val Padel = Football

    /** Legacy Padel cyan palette — kept as an explicit "Cyan" option for the
     *  global color picker so users who liked it can still pick it, even though
     *  the Padel sport itself now uses the Football palette. */
    val Cyan = SportColorScheme(
        primary = Color.hex("3CFFFF"),
        onPrimary = Color.hex("000000"),
        secondary = Color.hex("0AD1D1"),
        onSecondary = Color.hex("000000"),
        metricsPrimary = Color.hex("20B3B3"),
        interactivePrimary = Color.hex("0A8C8E"),
        radarFill = Color.hex("3CFFFF"),
        radarFillOpacity = 0.10f,
        dataPoint = Color.hex("20B3B3"),
        dataPointInner = Color.hex("3CFFFF"),
        chartAreaBase = Color.hex("20B3B3"),
        chartLineStroke = Color.hex("20B3B3"),
        radarSecondaryStroke = Color.hex("ACFFFF"),
        interactiveStrokeColor = Color.hex("36E6E8"),
        barBorderColor = "#36e6e8",
        barBackground = "#0a8c8e",
        barBoxShadow = "inset 0 {2} {5.5} #40ACAC, inset 0 {15} {22} #2E7373, inset 0 {44} {31} {8} #0D8181"
    )

    fun forSport(sport: String): SportColorScheme {
        // Light theme is locked to the blue (Football) palette — sport
        // switching and the global palette picker only recolor the dark
        // theme. Reading ThemeController.isLight (Compose state) inside
        // composition also subscribes callers, so the palette snaps back
        // the moment the theme flips.
        if (ThemeController.isLight) return Football
        return when (sport.lowercase()) {
            "football" -> Football
            "tennis" -> Tennis
            "volleyball" -> Volleyball
            "basketball" -> Basketball
            "padel" -> Padel
            "cyan" -> Cyan
            else -> Football
        }
    }
}

// =============================================================================
// Business / Admin color system — from DesignSystem_Arena-Admin_Color
// =============================================================================

object BusinessColors {

    // -------------------------------------------------------------------------
    // Blue palette (occupancy / heatmap scale)
    // -------------------------------------------------------------------------
    object Blue {
        val B100 = Color.hex("0064B1")   // Blue/100 — lightest
        val B200 = Color.hex("004390")   // Blue/200
        val B300 = Color.hex("0A2E67")   // Blue/300
        val B400 = Color.hex("00245D")   // Blue/400
        val B500 = Color.hex("00143B")   // Blue/500 — darkest
    }

    // -------------------------------------------------------------------------
    // Text
    // -------------------------------------------------------------------------
    object Text {
        val OnSurfaceMuted = Color.hex("99B1BF")
    }

    // -------------------------------------------------------------------------
    // Status
    // -------------------------------------------------------------------------
    object Status {
        val Declined = Color.hex("760606")
    }

    // -------------------------------------------------------------------------
    // Booking
    // -------------------------------------------------------------------------
    object Booking {
        val Background = Color.hex("042A67")
    }

    // -------------------------------------------------------------------------
    // Modal
    // -------------------------------------------------------------------------
    object Modal {
        val Background = Color.hex("030F26")
        val IconDefault = Color.hex("818D9C")
        val TextButton = Color.hexa("99B1BF", 0.50f)
    }

    // -------------------------------------------------------------------------
    // UI Surfaces
    // -------------------------------------------------------------------------
    object Surface {
        val ClientRowHover = Color.hex("09142D")
        val EmptyStateGraphic = Color.hex("0F1729")
        val DayContainerHover = Color.hex("091A40")
        val EmptyImagePrimary = Color.hex("192133")
        val EmptyImageSecondary = Color.hex("070C1F")
        val TimeBlockBackground = Color.hexa("000000", 0.30f)
    }

    // -------------------------------------------------------------------------
    // Effects (inner shadows) — CSS box-shadow strings
    // -------------------------------------------------------------------------
    object Effects {
        /** Inner shadow for circular chart (Booking Status) — blur 4px */
        const val ChartInnerShadow = "inset 0px 4px 4px rgba(0, 0, 0, 0.20)"
        /** Inner shadow for booking cards — blur 2px */
        const val BookingShadow = "inset 0px 4px 2px rgba(0, 0, 0, 0.20)"
    }

    // -------------------------------------------------------------------------
    // Field stroke gradients (linear-gradient, -90deg, mixed border-width)
    // left 5px; top, right, bottom 0.2px
    // -------------------------------------------------------------------------
    object FieldStroke {
        /** Field 01 — blue accent */
        const val Stroke01 = "linear-gradient(-90deg, rgba(0, 97, 255, 0.90), rgba(60, 121, 241, 0.35))"
        /** Field 02 — green accent */
        const val Stroke02 = "linear-gradient(-90deg, rgba(52, 213, 36, 1.00), rgba(60, 121, 241, 0.35))"
        /** Field 03 — orange accent */
        const val Stroke03 = "linear-gradient(-90deg, rgba(232, 137, 8, 1.00), rgba(60, 121, 241, 0.35))"
    }

    // -------------------------------------------------------------------------
    // Slot border
    // -------------------------------------------------------------------------
    object Slot {
        val Border = Color.hexa("949598", 0.30f)
    }
}

// =============================================================================
// Gradient CSS values (use with styleModifier { property("background", ...) })
// =============================================================================

object AppGradients {
    // ⚠ LIGHT-THEME: most members resolve through --arena-grad-* tokens
    // (AppTheme.kt) so they flip with data-theme. Dark values are the exact
    // strings that used to live here. Sport-tinted gradient FUNCTIONS below
    // and the stepper/chat-ring gradients stay literal (theme-invariant).

    // Surface gradients (for Player Overview, Team Overview blocks)
    val SurfaceGradient01 = "var(--arena-grad-surface-01)"
    val SurfaceGradient02 = "var(--arena-grad-surface-02)"

    // Surface inner gradient (for sections inside Player Overview)
    val SurfaceInnerGradientL = "var(--arena-grad-surface-inner-l)"

    // Interactive secondary — tuned on the New Chat bottom sheet (tab /
    // input / Cancel) then promoted here as the shared value.
    val InteractiveSecondary = "var(--arena-grad-interactive-secondary)"

    // Sport-dropdown icon circle fill — mirrors the mobile app's
    // AppGradients.SportIconCircleFill.
    val SportIconCircleFill = "var(--arena-grad-sport-circle-fill)"

    // Hairline ring paired with SportIconCircleFill (stroke "Border/gradient/01").
    val SportIconCircleBorder = "var(--arena-grad-sport-circle-border)"

    // Sport-dropdown panel border (Figma 11463:433626 stroke).
    val SportDropdownBorder = "var(--arena-grad-sport-dropdown-border)"

    // Soft grey section divider with transparent ends — mirrors the mobile
    // app's AppGradients.PanelDivider ("Section Underline" hairline).
    val PanelDivider = "var(--arena-grad-panel-divider)"

    // Score controls toggle
    val ScoreControlToggle = "var(--arena-grad-score-toggle)"

    // Stepper gradients
    val StepperIncrement =
        "radial-gradient(circle, rgba(0, 229, 164, 1), rgba(255, 255, 255, 1))"
    val StepperDecrement =
        "radial-gradient(circle, rgba(255, 26, 26, 1), rgba(255, 255, 255, 1))"

    // Overlay edge gradient (for card edges, fading content into the page
    // background — fades to near-black on dark, to the light canvas on light)
    val OverlayEdge = "var(--arena-grad-overlay-edge)"

    // Overlay players gradient (bottom-up fade)
    val OverlayPlayers = "var(--arena-grad-overlay-players)"

    // Match info overlay (same as players)
    val MatchInfoOverlay = OverlayPlayers

    // Overlay partial gradient (left-to-right fade for content overlays)
    val OverlayPartialGradient = "var(--arena-grad-overlay-partial)"

    // Overlay players horizontal (left-to-right, distinct from vertical OverlayPlayers)
    val OverlayPlayersHorizontal = "var(--arena-grad-overlay-players-h)"

    // Chart area fill gradients per sport
    fun chartAreaGradient(sport: String): String {
        val base = SportColors.forSport(sport).chartAreaBase.toRgb()
        val r = base.red; val g = base.green; val b = base.blue
        return "linear-gradient(to bottom, " +
            "rgba($r, $g, $b, 0.60) 0%, " +
            "rgba($r, $g, $b, 0.30) 33%, " +
            "rgba($r, $g, $b, 0.10) 66%, " +
            "rgba($r, $g, $b, 0.02) 100%)"
    }

    // Radar data point radial gradient per sport
    fun dataPointGradient(sport: String): String {
        val scheme = SportColors.forSport(sport)
        val outer = scheme.dataPoint.toRgb()
        val inner = scheme.dataPointInner.toRgb()
        return "radial-gradient(circle, " +
            "rgba(${outer.red}, ${outer.green}, ${outer.blue}, 1), " +
            "rgba(${inner.red}, ${inner.green}, ${inner.blue}, 0.50))"
    }

    // =========================================================================
    // Border / Stroke gradients
    // =========================================================================

    // Border for Player/Team Overview blocks (1px, -88deg)
    val BorderGradient01 = "var(--arena-grad-border-01)"

    // News/Matches card hairline — flat mid-saturation blue on all four
    // sides (the gradient sweeps read oddly on these cards).
    val NewsCardBorder = "var(--arena-news-card-border)"

    // Inner section border (1px, -84deg, inside)
    val BorderInnerL = "var(--arena-grad-border-inner-l)"

    // Button / inactive interactive element border (0.3px, 180deg, inside)
    val ButtonBorderGradientPrimary = "var(--arena-grad-btn-border)"

    // Floating panel border (0.8px, 180deg) — brighter white than the button
    // token so the stroke stays visible over the near-black app background.
    // Figma bottom-nav (10271:223391) renders this as ~0.4 white at the top edge.
    val PanelBorderGradient = "var(--arena-grad-panel-border)"

    // Mobile bottom-sheet hairline (1px, 180deg) — top edge is an OPAQUE but
    // muted navy on dark (#1D3358) so it reads as a solid line rather than a
    // see-through tint; light theme swaps in a periwinkle hairline.
    val SheetBorderGradient = "var(--arena-grad-sheet-border)"

    // Interactive bar chart stroke gradient per sport (0.8px, 180deg, inside)
    fun interactiveStrokeGradient(sport: String): String {
        val c = SportColors.forSport(sport).interactiveStrokeColor.toRgb()
        return "linear-gradient(180deg, " +
            "rgba(${c.red}, ${c.green}, ${c.blue}, 1), " +
            "rgba(102, 102, 102, 0))"
    }

    // =========================================================================
    // Chat stats ring strokes (radial gradient, 4.4px)
    // =========================================================================

    val ChatStatsWin =
        "conic-gradient(from 99deg, " +
            "rgba(0, 97, 255, 1) 0%, " +
            "rgba(104, 141, 202, 1) 69%, " +
            "rgba(17, 83, 187, 0) 95%)"

    val ChatStatsLosses =
        "conic-gradient(from 83deg, " +
            "rgba(65, 8, 2, 1) 0%, " +
            "rgba(227, 95, 95, 1) 81%, " +
            "rgba(177, 177, 177, 0) 95%)"

    val ChatStatsDraws =
        "conic-gradient(from 80deg, " +
            "rgba(27, 163, 40, 1) 23%, " +
            "rgba(180, 204, 246, 1) 42%, " +
            "rgba(21, 151, 21, 1) 69%)"
}
